import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Users, HeartPulse, FileText, Brain, ClipboardList,
  Stethoscope, LogOut, RefreshCw, CheckCircle2, ChevronRight, Settings as SettingsIcon, CalendarCheck, BellRing, CalendarClock,
} from "lucide-react";
import { MtgLogo } from "@/components/MtgLogo";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, tid: "overview", end: true },
  { to: "/retention", label: "Retention Engine", icon: HeartPulse, tid: "retention" },
  { to: "/patients", label: "Patients", icon: Users, tid: "patients" },
  { to: "/builder", label: "Plan Builder", icon: Stethoscope, tid: "treatment-builder" },
  { to: "/notes", label: "Clinical Notes", icon: FileText, tid: "clinical-notes" },
  { to: "/brain", label: "Clinical Brain", icon: Brain, tid: "clinical-brain" },
  { to: "/reception", label: "AI Reception", icon: CalendarCheck, tid: "ai-reception" },
  { to: "/appointments", label: "Appointments", icon: CalendarClock, tid: "appointments" },
  { to: "/recalls", label: "Recalls", icon: BellRing, tid: "recalls" },
  { to: "/history", label: "Plan History", icon: ClipboardList, tid: "plan-history" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "settings" },
];

export function useClinikoSync() {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null); // {running, synced, total}

  const load = () => api.get("/cliniko/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!progress?.running) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/cliniko/sync/status");
        setProgress(data);
        if (!data.running) {
          clearInterval(id);
          load();
          if (data.error) toast.error("Cliniko sync failed — retry");
          else toast.success(`Synced ${data.synced_records} patients from Cliniko`);
        }
      } catch { clearInterval(id); }
    }, 1500);
    return () => clearInterval(id);
  }, [progress?.running]);

  const sync = async () => {
    try {
      const { data } = await api.post("/cliniko/sync");
      if (data.running) {
        setProgress({ running: true, synced: 0, total: null });
        toast.info(data.message || "Cliniko sync started");
      } else {
        setStatus((s) => ({ ...s, ...data, connected: true }));
        toast.success(data.message || "Cliniko synced");
      }
    } catch (e) {
      toast.error("Cliniko sync failed — retry");
    }
  };

  return { status, progress, sync };
}

function syncDotClass(running, connected) {
  if (running) return "bg-amber-400";
  if (connected) return "bg-emerald-500";
  return "bg-slate-500";
}
function syncLabel(running, connected) {
  if (running) return "Syncing Cliniko…";
  if (connected) return "Cliniko synced";
  return "Not synced";
}
function syncCountText(running, connected, progress, status, total) {
  if (running) return `${progress?.synced ?? 0}${progress?.total ? ` / ${progress.total}` : ""} patients`;
  if (connected) return `${status?.synced_records ?? 0}${total ? ` / ${total}` : ""} patients`;
  return "tap the icon to sync";
}
function badgeCountText(running, connected, progress, status, total) {
  if (running) return `Syncing ${progress?.synced ?? 0}${progress?.total ? ` / ${progress.total}` : ""}…`;
  if (connected) return `${status?.synced_records ?? 0}${total ? ` / ${total}` : ""} patients`;
  return "retry sync";
}


export function SidebarSync({ status, progress, sync }) {
  const running = progress?.running;
  const connected = status?.connected;
  const total = status?.cliniko_total;
  return (
    <div className="mx-3 mb-1 rounded-xl border border-slate-800 bg-slate-800/50 px-3 py-2.5" data-testid="sidebar-sync">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            {(connected || running) && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${running ? "bg-amber-400" : "bg-emerald-400"} opacity-75`} />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${syncDotClass(running, connected)}`} />
          </span>
          <span className="text-[11px] font-semibold text-slate-200" data-testid="sidebar-sync-label">
            {syncLabel(running, connected)}
          </span>
        </div>
        <button onClick={sync} disabled={running} data-testid="sidebar-sync-btn" title="Sync all patients"
          className="text-slate-400 transition-colors hover:text-teal-300 disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${running ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="mt-1 font-mono text-[10px] text-slate-500" data-testid="sidebar-sync-count">
        {syncCountText(running, connected, progress, status, total)}
      </div>
    </div>
  );
}

export function ClinikoBadge({ status, progress, sync }) {
  const running = progress?.running;
  const connected = status?.connected;
  const total = status?.cliniko_total;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2 shadow-sm" data-testid="cliniko-sync-badge">
      <span className={`relative flex h-2.5 w-2.5 ${connected ? "" : "opacity-40"}`}>
        {connected && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-400"}`} />
      </span>
      <div className="hidden sm:block leading-tight">
        <div className="text-[11px] font-semibold text-slate-700">
          Cliniko {connected ? "Connected" : "Not loaded"}
        </div>
        <div className="font-mono text-[10px] text-slate-400" data-testid="cliniko-sync-progress">
          {badgeCountText(running, connected, progress, status, total)}
        </div>
      </div>
      <button
        onClick={sync}
        disabled={running}
        data-testid="cliniko-sync-now-btn"
        className="ml-1 inline-flex items-center gap-1.5 rounded-lg bg-teal-50 px-2.5 py-1.5 text-[11px] font-semibold text-teal-700 transition-colors hover:bg-teal-100 disabled:opacity-60"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${running ? "animate-spin" : ""}`} />
        {running ? "Syncing" : "Sync all"}
      </button>
    </div>
  );
}

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const clinikoSync = useClinikoSync();

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 flex w-64 flex-shrink-0 flex-col justify-between border-r border-slate-800 bg-slate-900 text-slate-100">
        <div>
          <div className="flex items-center px-5 py-5">
            <MtgLogo withText className="h-6 w-6" name={settings.business_name} logoUrl={settings.logo_url} tagline={settings.tagline} />
          </div>
          <SidebarSync {...clinikoSync} />
          <nav className="mt-2 space-y-1 px-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`sidebar-nav-${n.tid}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                    isActive
                      ? "bg-teal-600/15 text-teal-300 ring-1 ring-inset ring-teal-500/30"
                      : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <n.icon className={`h-4.5 w-4.5 ${isActive ? "text-teal-400" : ""}`} style={{ height: 18, width: 18 }} />
                    <span className="flex-1">{n.label}</span>
                    {isActive && <ChevronRight className="h-3.5 w-3.5 text-teal-500/70" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="space-y-3 border-t border-slate-800 p-4">
          <div className="flex items-center gap-2 rounded-lg bg-slate-800/60 px-3 py-2 text-[11px] text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            <span>Copy-only · No auto-send</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-xs font-semibold text-slate-200">{user?.name || "Clinician"}</div>
              <div className="truncate font-mono text-[10px] text-slate-500">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn"
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-red-400"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="ml-64 flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/85 px-8 py-4 backdrop-blur">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {actions}
            <ClinikoBadge {...clinikoSync} />
          </div>
        </header>
        <main className="flex-1 px-8 py-7">{children}</main>
      </div>
    </div>
  );
}
